import { useState, useEffect, useRef, lazy, Suspense, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { latLngToCell } from 'h3-js';
import { LogoLink } from '@/components/LogoLink';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonLabel,
  IonText,
  IonButtons,
  createAnimation,
} from '@ionic/react';
import {
  briefcase,
  arrowForward,
  location,
  time,
  shield,
  rocket,
  sparkles,
  chatbubbleEllipses,
  logoLinkedin,
  logoGoogle,
  logoApple,
  notifications,
  flash,
  water,
  hammer,
  construct,
  brush,
  flame,
  informationCircle,
  school,
} from 'ionicons/icons';
import { useSearchStore } from '@/stores';
import { useAuthStore } from '@/stores/useAuthStore';
import { signOut } from '@/lib/api/auth';
import { useRecentJobs, useRecentCandidates, useMarketplaceStats, useSearchProviders } from '@/lib/api/hooks';
import { RecentViews } from '@/components/RecentViews';
import { OfoAutocomplete } from '@/components/ofo/OfoAutocomplete';
import type { OfoSuggestion } from '@/lib/ofo-client-search';
import { AppFooter } from '@/components/AppFooter';
import { ProviderCarousel } from '@/components/providers/ProviderCarousel';
import { LocationBadge } from '@/components/LocationBadge';
import BoringAvatar from '@/components/BoringAvatar';
import { useLocationContext } from '@/lib/geo/LocationContext';
import { loadExploreGraph } from '@/lib/ofo-explore';
import { setSeo, resetSeo, BASE_URL } from '@/lib/seo';
import { useMapDensity, zoomToH3ResolutionAndK } from '@/lib/api/hooks';
import { people } from 'ionicons/icons';

const LazyMapCanvas = lazy(() => import('@/components/map/MapCanvas').then((m) => ({ default: m.MapCanvas })));
const LazyDensityLayer = lazy(() => import('@/components/map/DensityLayer').then((m) => ({ default: m.DensityLayer })));

function AnimatedCounter({ target, label }: { target: number; label: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="bp-stat-pill">
      <div className="bp-stat-number">{count.toLocaleString()}+</div>
      <div className="bp-stat-label">{label}</div>
    </div>
  );
}

const categories = [
  { icon: flash, label: 'Electrical', count: 0, color: '#3b82f6', ofoCode: '671101', img: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=200&h=200&fit=crop' },
  { icon: water, label: 'Plumbing', count: 0, color: '#10b981', ofoCode: '642601', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&h=200&fit=crop' },
  { icon: construct, label: 'Carpentry', count: 0, color: '#8b5cf6', ofoCode: '641501', img: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&h=200&fit=crop' },
  { icon: flame, label: 'Welding', count: 0, color: '#06b6d4', ofoCode: '651202', img: 'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=200&h=200&fit=crop' },
  { icon: hammer, label: 'Mechanic', count: 0, color: '#f59e0b', ofoCode: '653101', img: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200&h=200&fit=crop' },
  { icon: brush, label: 'Plastering', count: 0, color: '#ec4899', ofoCode: '642302', img: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=200&h=200&fit=crop' },
];

const features = [
  {
    icon: sparkles,
    title: 'AI-Powered Matching',
    desc: 'Our algorithm matches your trade, location, and availability to real contract and project work.',
  },
  {
    icon: rocket,
    title: 'One-Tap Apply',
    desc: 'Build your profile once, then apply to contract jobs across South Africa with a single tap.',
  },
  {
    icon: shield,
    title: 'Verified Pros & Clients',
    desc: 'Every pro and client on BluePages is vetted. No scams, no ghost jobs — only real project work.',
  },
  {
    icon: chatbubbleEllipses,
    title: 'Direct Messaging',
    desc: 'Skip the middleman. Chat directly with homeowners and businesses in real time.',
  },
];

/** Compact relative-time formatter for "posted X ago" labels. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.floor((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.floor(diffDay / 30);
  return `${diffMo}mo ago`;
}

function salaryLabel(job: { salaryMin?: number; salaryMax?: number; desiredSalaryMin?: number; desiredSalaryMax?: number }): string {
  const min = job.salaryMin ?? job.desiredSalaryMin;
  const max = job.salaryMax ?? job.desiredSalaryMax;
  if (min == null && max == null) return 'Rate negotiable';
  if (min != null && max != null) {
    return `R${min.toLocaleString()} – R${max.toLocaleString()}`;
  }
  const v = (min ?? max)!;
  return `R${v.toLocaleString()}`;
}

const testimonials = [
  {
    name: 'Sipho Dlamini',
    role: 'Qualified Electrician',
    quote: 'BluePages sent me six callouts in my first week. The contract work floods in daily — no more quiet Mondays.',
    avatar: 'https://i.pravatar.cc/80?img=11',
  },
  {
    name: 'Maria van Wyk',
    role: 'Handyman, Cape Town',
    quote: 'I post my availability and homeowners book me for project work instantly. Steady contract jobs every week now.',
    avatar: 'https://i.pravatar.cc/80?img=5',
  },
  {
    name: 'John Petersen',
    role: 'Plumbing Contractor',
    quote: 'The matching puts real project work in front of me — geyser swaps, renovations, emergencies. Game changer.',
    avatar: 'https://i.pravatar.cc/80?img=12',
  },
];

const trustedBy = [
  'Pam Golding Properties',
  'Seeff Properties',
  'Builders Warehouse',
  'Plate Tamper Hire',
  'SA Property Owners',
];

// Example phrases that showcase the natural-language / synonym-aware OFO search.
// Clicking one fills the search box so occupation suggestions appear immediately.
const exampleQueries = [
  'I fix cars',
  'nurse sick people',
  'build houses',
  'weld metal',
  'cook food',
  'teach children',
  'cut hair',
  'drive trucks',
];

// Occupation-representative images that crossfade behind the hero. Each maps to
// a trade or profession on the BluePages marketplace (electrical, plumbing,
// carpentry, welding, painting, mechanical, nursing, culinary, teaching, transport).
// Reduced from 10→6 to cut mobile network + GPU memory pressure.
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&h=500&fit=crop&q=75',
];

export function LandingPage() {
  const history = useHistory();
  const { addToHistory } = useSearchStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { h3Origin, name: locName } = useLocationContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [hasResults, setHasResults] = useState(false);
  const tryRef = useRef<HTMLDivElement>(null);
  const { data: statsData } = useMarketplaceStats();
  const stats = statsData?.data;

  // Adopt the searcher's chosen origin (detected or manually picked) so results
  // rank by proximity. Empty when no location is set.
  const locParams = h3Origin
    ? `&h3Origin=${encodeURIComponent(h3Origin)}&near=${encodeURIComponent(locName ?? '')}`
    : '';

  // Best-effort background prefetch of the occupation explorer graph so
  // /explore opens instantly. Defer to idle time to avoid blocking the
  // landing page first paint on mobile.
  useEffect(() => {
    const idle = (cb: () => void) => {
      if ('requestIdleCallback' in window) return requestIdleCallback(cb, { timeout: 5000 });
      return setTimeout(cb, 1000);
    };
    const id = idle(() => loadExploreGraph().catch(() => {}));
    return () => { if (typeof id === 'number') clearTimeout(id); else cancelAnimationFrame?.(id as unknown as number); };
  }, []);

  // Crossfade the hero background through the occupation image set.
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);
  // Preload only the first 3 hero images — the rest load on demand as the
  // carousel rotates. Cuts initial network + GPU memory on mobile.
  useEffect(() => {
    HERO_IMAGES.slice(0, 3).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Rotate hero text between candidate and employer perspectives.
  const HERO_TEXTS = [
    {
      heading: ['Find Contract Work That ', 'Fits Your Trade'],
      sub: 'Connect with homeowners and businesses across South Africa. Find contract and project work, get matched by AI, and land your next job — all from your phone.',
    },
    {
      heading: ['Hire Skilled Tradespeople ', 'You Can Trust'],
      sub: 'Post jobs and find verified electricians, plumbers, carpenters, and more. AI-matched candidates, real reviews, POPIA-compliant — hire with confidence.',
    },
  ];
  const [heroTextIdx, setHeroTextIdx] = useState(0);
  const [heroTextVisible, setHeroTextVisible] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setHeroTextVisible(false);
      setTimeout(() => {
        setHeroTextIdx((i) => (i + 1) % HERO_TEXTS.length);
        setHeroTextVisible(true);
      }, 400);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  // Platform SEO + WebSite structured data (sitelinks search box).
  useEffect(() => {
    setSeo({
      canonical: `${BASE_URL}/home`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'BluePages',
        url: BASE_URL,
        inLanguage: 'en-ZA',
        publisher: { '@type': 'Organization', name: 'BluePages', url: BASE_URL },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    });
    return () => resetSeo();
  }, []);

  // Most recently added, publicly-viewable job listings for the Featured section.
  const { data: recentJobsData, isLoading: recentJobsLoading } = useRecentJobs(6);
  const featuredJobs = (recentJobsData?.data ?? []).slice(0, 6);

  const { data: recentCandidatesData, isLoading: recentCandidatesLoading } = useRecentCandidates(10);

  const { data: providersData } = useSearchProviders({ pageSize: 6 });
  const featuredCandidates = (recentCandidatesData?.data ?? []).slice(0, 10);

  // ── Explore Map ──
  const [mapMetric, setMapMetric] = useState<'candidates' | 'jobs' | 'providers'>('candidates');
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapViewCenter, setMapViewCenter] = useState<[number, number] | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(5);
  const [selectedHex, setSelectedHex] = useState<{ h3: string; coords: [number, number] } | null>(null);
  const prevOriginRef = useRef<string | null>(null);

  // Default centre: Gauteng cluster (highest candidate density) at zoom 5.
  const DEFAULT_MAP_CENTER: [number, number] = [28.03, -26.06];

  // Derive H3 resolution + k ring size from current zoom level.
  const { resolution: mapQueryResolution, k: mapQueryK } = useMemo(
    () => zoomToH3ResolutionAndK(mapZoom),
    [mapZoom],
  );

  // Compute H3 origin cell — only update state when the cell actually changes.
  // This prevents unnecessary refetches on every pixel of pan.
  const mapQueryOrigin = useMemo(() => {
    const c = mapViewCenter ?? DEFAULT_MAP_CENTER;
    try {
      return latLngToCell(c[1], c[0], mapQueryResolution);
    } catch {
      return latLngToCell(DEFAULT_MAP_CENTER[1], DEFAULT_MAP_CENTER[0], mapQueryResolution);
    }
  }, [mapViewCenter, mapQueryResolution]);

  const stableOrigin = prevOriginRef.current === mapQueryOrigin
    ? prevOriginRef.current
    : (prevOriginRef.current = mapQueryOrigin);

  const { data: densityData } = useMapDensity(
    stableOrigin,
    mapQueryK,
    mapMetric,
    true,
    mapQueryResolution,
  );

  const handleMapReady = useCallback((map: any) => {
    setMapInstance(map);
  }, []);

  // Refetch density when the user pans or zooms the map.
  const handleMoveEnd = useCallback((center: [number, number], zoom: number) => {
    const clamped = Math.round(zoom);
    setMapViewCenter((prev) =>
      prev && prev[0] === center[0] && prev[1] === center[1] ? prev : center,
    );
    setMapZoom((prev) => (prev === clamped ? prev : clamped));
  }, []);

  // Handle hex cell click — show candidates at that location.
  const handleHexClick = useCallback((h3Index: string, coords: [number, number]) => {
    setSelectedHex({ h3: h3Index, coords });
  }, []);

  // Crossfade the "Try" suggestion chips out (and back in) using an Ionic
  // animation whenever live results appear/disappear beneath the search bar.
  useEffect(() => {
    const el = tryRef.current;
    if (!el) return;
    const full = `${el.scrollHeight}px`;
    const anim = createAnimation()
      .addElement(el)
      .duration(260)
      .easing('cubic-bezier(0.4, 0, 0.2, 1)')
      .fromTo('opacity', hasResults ? '1' : '0', hasResults ? '0' : '1')
      .fromTo('maxHeight', hasResults ? full : '0px', hasResults ? '0px' : full)
      .fromTo('marginTop', hasResults ? '12px' : '0px', hasResults ? '0px' : '12px')
      .fromTo('transform', hasResults ? 'translateY(0)' : 'translateY(-6px)', hasResults ? 'translateY(-6px)' : 'translateY(0)');
    el.style.pointerEvents = hasResults ? 'none' : 'auto';
    el.style.overflow = 'hidden';
    anim.play();
    return () => { anim.destroy(); };
  }, [hasResults]);

  const handleSelectOFO = (ofo: OfoSuggestion) => {
    addToHistory(`${ofo.code} - ${ofo.description}`);
    history.push(`/search?ofoCode=${ofo.code}&q=${encodeURIComponent(ofo.description)}${locParams}`);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      addToHistory(searchQuery);
      history.push(`/search?q=${encodeURIComponent(searchQuery.trim())}${locParams}`);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <LogoLink style={{ height: '56px', marginTop: '4px' }} />
          </IonTitle>
          <IonButtons slot="end">
            {isAuthenticated ? (
              <IonButton onClick={() => signOut().then(() => history.replace('/home'))}>
                Sign out
              </IonButton>
            ) : (
              <IonButton onClick={() => history.push('/signin')}>Sign in</IonButton>
            )}
            <IonButton><IonIcon icon={notifications} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* ── Hero ── */}
      <div className="bp-hero" style={{ position: 'relative' }}>
        <div className="bp-hero-bg" aria-hidden="true">
          {HERO_IMAGES.map((src, i) => (
            <div
              key={src}
              className="bp-hero-slide"
              style={{
                backgroundImage: `url('${src}')`,
                opacity: i === heroIdx ? 1 : 0,
                transform: i === heroIdx ? 'scale(1.08)' : 'scale(1)',
              }}
            />
          ))}
          <div className="bp-hero-slide-overlay" />
        </div>
        <div className="bp-glow-orb bp-glow-orb-1" />
        <div className="bp-glow-orb bp-glow-orb-2" />

        <div className="bp-hero-content">
          <div className="bp-badge" style={{ marginBottom: '1.25rem' }}>
            <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
            South Africa's Contract & Project Marketplace
          </div>

          <h1
            className="bp-heading"
            style={{
              fontSize: '2rem', marginBottom: '0.75rem',
              opacity: heroTextVisible ? 1 : 0,
              transform: heroTextVisible ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}
          >
            {HERO_TEXTS[heroTextIdx].heading[0]}
            <span style={{ background: 'var(--bp-gradient-accent)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {HERO_TEXTS[heroTextIdx].heading[1]}
            </span>
          </h1>

          <p
            className="bp-subheading"
            style={{
              marginBottom: '1.75rem', maxWidth: '480px',
              opacity: heroTextVisible ? 1 : 0,
              transform: heroTextVisible ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.4s ease 0.05s, transform 0.4s ease 0.05s',
            }}
          >
            {HERO_TEXTS[heroTextIdx].sub}
          </p>

          <LocationBadge />

          <div className="bp-search-focus" style={{ position: 'relative', marginTop: '1.2em' }}>
            <label className="bp-search-label">Find a trade, skill, or project</label>
            <OfoAutocomplete
              value={searchQuery}
              onChange={setSearchQuery}
              onSelect={handleSelectOFO}
              onSearch={handleSearch}
              onResultsChange={setHasResults}
              placeholder="Electrician, handyman, plumber…"
            />

            <div
              ref={tryRef}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '12px',
              }}
            >
              <span style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted)' }}>Try:</span>
              {exampleQueries.map((q) => (
                <IonChip
                  key={q}
                  onClick={() => setSearchQuery(q)}
                  style={{ '--background': 'rgba(255,255,255,0.04)', '--color': 'var(--bp-text-secondary)', borderColor: 'var(--bp-border-subtle)', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  <IonLabel>{q}</IonLabel>
                </IonChip>
              ))}
            </div>
          </div>
        </div>
        </div>

        {/* ── Service Agent Promotion ── */}
        <div className="bp-section" style={{ padding: '2rem 1.25rem' }}>
          <div className="bp-agent-promo-card">
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', maxWidth: '1000px', margin: '0 auto' }}>
              <div style={{ flex: '0 0 120px', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', lineHeight: '1' }}>👷</div>
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--bp-text)' }}>
                    Become a Service Agent
                  </h2>
                  <p style={{ margin: '0 0 1rem', fontSize: '0.95rem', color: 'var(--bp-text-secondary)', lineHeight: 1.6 }}>
                    Own your local marketplace. Support candidates, connect employers, and grow South Africa's contract workforce in your community.
                  </p>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <IonButton
                    size="small"
                    routerLink="/agents"
                    style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0.5rem 1rem' }}
                  >
                    Find an Agent
                    <IonIcon slot="end" icon={arrowForward} />
                  </IonButton>
                  <IonButton
                    size="small"
                    routerLink="/agents/apply"
                    fill="outline"
                    style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0.5rem 1rem' }}
                  >
                    Apply Now
                  </IonButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats ── */}
      <div className="bp-section">
        <IonGrid style={{ padding: 0 }}>
          <IonRow>
            <IonCol size="4"><AnimatedCounter target={stats?.openContracts ?? 0} label="Open Contracts" /></IonCol>
            <IonCol size="4"><AnimatedCounter target={stats?.skilledTradespeople ?? 0} label="Skilled Tradespeople" /></IonCol>
            <IonCol size="4"><AnimatedCounter target={stats?.homeownersAndBusinesses ?? 0} label="Homeowners & Businesses" /></IonCol>
          </IonRow>
        </IonGrid>
      </div>

      {/* ── Explore the Map ── */}
      <div className="bp-section" style={{ padding: '0 1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div>
            <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>Explore the Map</h2>
            <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>
              {mapMetric === 'candidates' && 'Skilled candidates across South Africa'}
              {mapMetric === 'jobs' && 'Open jobs across South Africa'}
              {mapMetric === 'providers' && 'Training providers across South Africa'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <IonButton
              fill={mapMetric === 'candidates' ? 'solid' : 'clear'}
              size="small"
              onClick={() => setMapMetric('candidates')}
              style={{ fontSize: '0.75rem', '--padding-start': '8px', '--padding-end': '8px' }}
            >
              <IonIcon icon={people} slot="start" /> Candidates
            </IonButton>
            <IonButton
              fill={mapMetric === 'jobs' ? 'solid' : 'clear'}
              size="small"
              onClick={() => setMapMetric('jobs')}
              style={{ fontSize: '0.75rem', '--padding-start': '8px', '--padding-end': '8px' }}
            >
              <IonIcon icon={briefcase} slot="start" /> Jobs
            </IonButton>
            <IonButton
              fill={mapMetric === 'providers' ? 'solid' : 'clear'}
              size="small"
              onClick={() => setMapMetric('providers')}
              style={{ fontSize: '0.75rem', '--padding-start': '8px', '--padding-end': '8px' }}
            >
              <IonIcon icon={school} slot="start" /> Providers
            </IonButton>
          </div>
        </div>
        <Suspense fallback={<div className="bp-map-lift" style={{ height: 'min(85vh, 850px)', background: 'var(--ion-color-step-80)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ion-color-medium)', fontSize: '0.8125rem' }}>Loading map…</div>}>
          {/* Shadow wrapper sits outside MapLibre — Firefox WebGL ignores parent radius clip */}
          <div className="bp-map-lift">
            <LazyMapCanvas
              center={DEFAULT_MAP_CENTER}
              zoom={5}
              minZoom={3}
              maxZoom={14}
              style={{ height: 'min(85vh, 850px)', borderRadius: 12 }}
              onMapReady={handleMapReady}
              onMoveEnd={handleMoveEnd}
              onHexClick={handleHexClick}
            >
              {mapInstance && densityData && (
                <LazyDensityLayer
                  map={mapInstance}
                  data={densityData}
                  opacity={0.45}
                />
              )}
            </LazyMapCanvas>
            {/* Hex click panel — floats over bottom-right of map */}
            {selectedHex && (() => {
              // Find the clicked hex in density data to get its count.
              const densityFeatures = densityData?.features ?? [];
              const clickedFeature = densityFeatures.find(
                (f: any) => (f.properties?.h3Index ?? f.properties?.h3) === selectedHex.h3,
              );
              const count = (clickedFeature?.properties as any)?.count ?? 0;
              const suppressed = (clickedFeature?.properties as any)?.suppressed;
              const areaLabel = (clickedFeature?.properties as any)?.label;
              return (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    width: 240,
                    background: 'rgba(15,23,42,0.92)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 10,
                    padding: '14px',
                    zIndex: 2,
                    color: '#f8fafc',
                    fontSize: '0.8125rem',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      {mapMetric === 'candidates' ? 'Candidates' : mapMetric === 'jobs' ? 'Jobs' : 'Training Providers'}
                    </span>
                    <button
                      onClick={() => setSelectedHex(null)}
                      style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.125rem', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
                    >
                      ✕
                    </button>
                  </div>
                  {suppressed ? (
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: 10 }}>
                      Not enough data in this area (k-anonymity).
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.1, marginBottom: 4 }}>
                        {count}
                      </div>
                      {areaLabel && (
                        <div style={{ fontWeight: 600, fontSize: '0.8125rem', marginBottom: 2 }}>
                          {areaLabel}
                        </div>
                      )}
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: 12 }}>
                        {mapMetric === 'candidates' ? 'candidates' : mapMetric === 'jobs' ? 'jobs' : 'training providers'} in this area
                      </div>
                    </>
                  )}
                  <IonButton
                    expand="block"
                    fill="solid"
                    size="small"
                    routerLink={`/search?tab=${mapMetric}&h3Origin=${selectedHex.h3}`}
                    style={{ '--background': '#3b82f6', '--color': '#fff', fontSize: '0.8125rem', fontWeight: 600, margin: 0 }}
                  >
                    View {mapMetric} here <IonIcon icon={arrowForward} slot="end" />
                  </IonButton>
                </div>
              );
            })()}
          </div>
        </Suspense>
        <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <IonButton fill="clear" routerLink="/search" style={{ '--color': 'var(--bp-blue-400)', fontSize: '0.8125rem', fontWeight: 600 }}>
            Search candidates & jobs <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.6875rem', color: 'var(--bp-text-muted)' }}>
          <span>Low</span>
          <div style={{ display: 'flex', height: 10, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: 24, background: '#3b82f6' }} />
            <div style={{ width: 24, background: '#8b5cf6' }} />
            <div style={{ width: 24, background: '#f59e0b' }} />
            <div style={{ width: 24, background: '#ef4444' }} />
          </div>
          <span>High</span>
          <span style={{ marginLeft: 8 }}>{mapMetric === 'candidates' ? 'candidates' : mapMetric === 'jobs' ? 'jobs' : 'providers'} per area</span>
        </div>
      </div>

      {/* ── Beta Notice ── */}
      <div className="bp-section" style={{ padding: '0 1.25rem', marginBottom: '1.2rem' }}>
        <div className="bp-beta-notice">
          <div className="bp-beta-icon">
            <IonIcon icon={informationCircle} />
          </div>
          <div className="bp-beta-body">
            <div className="bp-beta-heading">
              <span className="bp-beta-badge">Alpha</span>
              BluePages is in active development
            </div>
            <p className="bp-beta-text">
              You're using an early version of BluePages. Some features may not work as expected.
              If you spot something off, we'd love to hear about it — your feedback helps us build a better platform.
            </p>
          </div>
        </div>
      </div>

      {/* ── Trusted By ── */}
      <div className="bp-section-alt">
        <p style={{ fontSize: '0.6875rem', color: 'var(--bp-text-muted)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem' }}>
           Trusted by homeowners & businesses across SA
         </p>
         <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
           {trustedBy.map((name) => (
             <span key={name} style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--bp-text-muted)', opacity: 0.6 }}>
               {name}
             </span>
           ))}
         </div>
      </div>

      <RecentViews />

      {/* ── Categories ── */}
      <div className="bp-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>Browse by Trade</h2>
            <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>Skilled contract work across every trade</p>
          </div>
          <IonButton fill="clear" onClick={() => history.push('/explore')} style={{ '--color': 'var(--bp-blue-400)', fontSize: '0.8125rem', fontWeight: 600 }}>
            View all <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>
        <IonGrid style={{ padding: 0 }}>
          <IonRow>
            {(stats?.categories ?? categories).map((cat) => {
              const base = categories.find(c => c.label === cat.label) ?? categories[0];
              return (
                <IonCol size="4" key={cat.label} style={{ padding: '4px' }}>
                  <div className="bp-category-card" role="button" tabIndex={0} onClick={() => history.push(`/search?tab=jobs&ofoCode=${cat.ofoCode}`)}>
                    <div className="bp-category-img">
                      <img src={base.img} alt={cat.label} loading="lazy" />
                    </div>
                    <div className="bp-category-label">{cat.label}</div>
                    <div className="bp-category-count">{cat.count.toLocaleString()} jobs</div>
                  </div>
                </IonCol>
              );
            })}
          </IonRow>
        </IonGrid>
      </div>

      {/* ── Featured Jobs + Candidates (side-by-side on desktop) ── */}
      <div className="bp-featured-grid">
        {/* ── Featured Jobs (most recently added, public listings) ── */}
        <div className="bp-section-alt bp-featured-col">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>Latest Jobs</h2>
            <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>New contract & project work, just posted</p>
          </div>
          <IonButton fill="clear" onClick={() => history.push('/search?tab=jobs')} style={{ '--color': 'var(--bp-blue-400)', fontSize: '0.8125rem', fontWeight: 600 }}>
            See all <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recentJobsLoading && (
            <>
              {[0, 1, 2].map((i) => (
                <div className="bp-job-card" key={i} style={{ opacity: 0.5 }}>
                  <div className="bp-job-card-img" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div className="bp-job-title">Loading…</div>
                  </div>
                </div>
              ))}
            </>
          )}
          {!recentJobsLoading && featuredJobs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: 'var(--bp-text-muted)', fontSize: '0.8125rem' }}>
              No open listings yet — check back soon.
            </div>
          )}
          {!recentJobsLoading && featuredJobs.map((job) => (
            <div
              className="bp-job-card"
              key={job._id}
              role="button"
              tabIndex={0}
              onClick={() => history.push(`/jobs/${job._id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') history.push(`/jobs/${job._id}`); }}
            >
              <div className="bp-job-card-img" style={{ borderRadius: 'var(--bp-radius-sm)', overflow: 'hidden' }}>
                <BoringAvatar name={job.title} size={64} square />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="bp-job-title">{job.title}</div>
                    <div className="bp-job-company">{job.workType} · Verified client</div>
                  </div>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--bp-text-muted)' }}>
                    {timeAgo(job.metadata.createdAt)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem', color: 'var(--bp-text-secondary)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IonIcon icon={location} style={{ fontSize: '0.75rem' }} /> {job.location || job.province}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IonIcon icon={time} style={{ fontSize: '0.75rem' }} /> {salaryLabel(job)}</span>
                </div>
                <div className="bp-job-tags">
                  {job.skillsRequired.slice(0, 4).map((tag) => (
                    <span key={tag} className="bp-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Featured Candidates (most recently registered, marketplace-visible) ── */}
      <div className="bp-section bp-featured-col">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>Latest Candidates</h2>
            <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>Skilled people, just joined the marketplace</p>
          </div>
          <IonButton fill="clear" onClick={() => history.push('/search?tab=candidates')} style={{ '--color': 'var(--bp-blue-400)', fontSize: '0.8125rem', fontWeight: 600 }}>
            See all <IonIcon icon={arrowForward} slot="end" />
          </IonButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recentCandidatesLoading && (
            <>
              {[0, 1, 2].map((i) => (
                <div className="bp-job-card" key={i} style={{ opacity: 0.5 }}>
                  <div className="bp-job-card-img" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div className="bp-job-title">Loading…</div>
                  </div>
                </div>
              ))}
            </>
          )}
          {!recentCandidatesLoading && featuredCandidates.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: 'var(--bp-text-muted)', fontSize: '0.8125rem' }}>
              No candidates in the marketplace yet — check back soon.
            </div>
          )}
          {!recentCandidatesLoading && featuredCandidates.map((candidate) => (
            <div
              className="bp-job-card"
              key={candidate._id}
              role="button"
              tabIndex={0}
              onClick={() => history.push(`/candidate/${candidate.slug}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') history.push(`/candidate/${candidate.slug}`); }}
            >
              <div className="bp-job-card-img" style={{ borderRadius: 'var(--bp-radius-sm)', overflow: 'hidden' }}>
                <BoringAvatar name={candidate.headline} size={64} square />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="bp-job-title">{candidate.headline}</div>
                    <div className="bp-job-company">{candidate.workType} · {candidate.availability}</div>
                  </div>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--bp-text-muted)' }}>
                    {timeAgo(candidate.metadata.createdAt)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem', color: 'var(--bp-text-secondary)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <IonIcon icon={location} style={{ fontSize: '0.75rem' }} />
                    {candidate.desiredLocations?.[0]?.label ?? candidate.plusCodeShort ?? 'South Africa'}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <IonIcon icon={time} style={{ fontSize: '0.75rem' }} /> {salaryLabel(candidate)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* ── Training Providers ── */}
      {providersData?.data?.hits && providersData.data.hits.length > 0 && (
        <div className="bp-section" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div className="bp-badge" style={{ marginBottom: '1rem' }}>
              <IonIcon icon={school} style={{ fontSize: '0.625rem' }} />
              Training Providers
            </div>
            <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>Upskill With Accredited Training</h2>
            <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>
              Find SETA-accredited providers for your trade
            </p>
          </div>
          <ProviderCarousel providers={providersData.data.hits.slice(0, 6)} />
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <IonButton fill="clear" routerLink="/search?tab=providers" routerDirection="forward">
              View all providers <IonIcon icon={arrowForward} slot="end" />
            </IonButton>
          </div>
        </div>
      )}

      {/* ── How It Works ── */}
      <div className="bp-section">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div className="bp-badge" style={{ marginBottom: '1rem' }}>
            <IonIcon icon={rocket} style={{ fontSize: '0.625rem' }} />
            How It Works
          </div>
          <h2 className="bp-heading" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Three Steps to Your Next Role</h2>
          <p className="bp-subheading" style={{ maxWidth: '400px', margin: '0 auto' }}>
            No lengthy forms. No phone screens. Just you and your next job.
          </p>
        </div>
        <div className="bp-how-image">
          <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=280&fit=crop" alt="Team collaborating" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
          {[
            { step: '01', title: 'Build Your Profile', desc: 'Add your skills, experience, and preferences in under 5 minutes.' },
            { step: '02', title: 'Get Matched', desc: 'Our AI surfaces roles that align with your goals and location.' },
            { step: '03', title: 'Connect & Hire', desc: 'Message clients directly. No recruiters, no fees, no gatekeeping.' },
          ].map((item) => (
            <div key={item.step} className="bp-feature-card">
              <div className="bp-feature-icon" style={{ background: 'var(--bp-gradient-accent)', color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>
                {item.step}
              </div>
              <div>
                <div className="bp-feature-title">{item.title}</div>
                <div className="bp-feature-desc">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <div className="bp-section-alt">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 className="bp-heading" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Why BluePages?</h2>
          <p className="bp-subheading" style={{ maxWidth: '400px', margin: '0 auto' }}>
            Built for the South African job market. Designed for how people actually search.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {features.map((f) => (
            <div className="bp-feature-card" key={f.title}>
              <div className="bp-feature-icon">
                <IonIcon icon={f.icon} />
              </div>
              <div>
                <div className="bp-feature-title">{f.title}</div>
                <div className="bp-feature-desc">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Testimonials ── */}
      <div className="bp-section">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 className="bp-heading" style={{ fontSize: '1.25rem', marginBottom: '4px' }}>What People Say</h2>
          <p className="bp-subheading" style={{ fontSize: '0.8125rem' }}>Real stories from pros and clients</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {testimonials.map((t) => (
            <div className="bp-testimonial" key={t.name}>
              <div className="bp-testimonial-quote">{t.quote}</div>
              <div className="bp-testimonial-author">
                <img src={t.avatar} alt={t.name} className="bp-testimonial-avatar" />
                <div>
                  <div className="bp-testimonial-name">{t.name}</div>
                  <div className="bp-testimonial-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="bp-cta">
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 className="bp-heading" style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>
            Ready to Find Your Next Contract?
          </h2>
          <p className="bp-subheading" style={{ maxWidth: '400px', margin: '0 auto 1.5rem' }}>
            Join 50,000+ South Africans already using BluePages. Free for pros — always.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
            <button className="bp-btn-primary" onClick={() => history.push('/register')}>
              <IonIcon icon={sparkles} />
              Create Your Profile
            </button>
            <button className="bp-btn-ghost" onClick={() => history.push('/search')}>
              <IonIcon icon={briefcase} />
              Browse Work
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1.5rem' }}>
            <IonIcon icon={logoLinkedin} style={{ fontSize: '1.25rem', color: 'var(--bp-text-muted)' }} />
            <IonIcon icon={logoGoogle} style={{ fontSize: '1.25rem', color: 'var(--bp-text-muted)' }} />
            <IonIcon icon={logoApple} style={{ fontSize: '1.25rem', color: 'var(--bp-text-muted)' }} />
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: '2rem 1.25rem 1rem', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { label: 'About', route: '/about' },
            { label: 'AI & Matching', route: '/ai-matching' },
            { label: 'Privacy', route: '/privacy' },
            { label: 'Terms', route: '/terms' },
            { label: 'Contact', route: '/contact' },
            { label: 'Help', route: '/help' },
            { label: 'Donate', route: '/donate' },
          ].map((link) => (
            <span
              key={link.route}
              onClick={() => history.push(link.route)}
              style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted)', cursor: 'pointer' }}
            >
              {link.label}
            </span>
          ))}
        </div>
        <IonText style={{ fontSize: '0.6875rem', color: 'var(--bp-text-muted)' }}>
          &copy; {new Date().getFullYear()} BluePages (Pty) Ltd. All rights reserved. A Rosta platform.
        </IonText>
      </div>
            <AppFooter />

      </IonContent>
    </IonPage>
  );
}
